import * as THREE from 'three';
import { countGeometryStats } from '../core/math';
import { createStoneMaterial, createViolinStringMaterial } from '../shaders/materials';
import type { InteractiveElement, SpecimenInstance, TemporalUniforms } from '../core/types';

export function createCremoneseViolinBodyGeometry(thickness = 0.14): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape();

  shape.moveTo(0, -1.0);
  shape.bezierCurveTo(0.42, -1.0, 0.64, -0.78, 0.62, -0.45);
  shape.bezierCurveTo(0.60, -0.22, 0.44, -0.14, 0.34, -0.12);
  shape.bezierCurveTo(0.27, -0.05, 0.27, 0.15, 0.34, 0.22);
  shape.bezierCurveTo(0.44, 0.26, 0.54, 0.38, 0.50, 0.62);
  shape.bezierCurveTo(0.46, 0.86, 0.24, 0.98, 0.0, 0.98);
  shape.bezierCurveTo(-0.24, 0.98, -0.46, 0.86, -0.50, 0.62);
  shape.bezierCurveTo(-0.54, 0.38, -0.44, 0.26, -0.34, 0.22);
  shape.bezierCurveTo(-0.27, 0.15, -0.27, -0.05, -0.34, -0.12);
  shape.bezierCurveTo(-0.44, -0.14, -0.60, -0.22, -0.62, -0.45);
  shape.bezierCurveTo(-0.64, -0.78, -0.42, -1.0, 0.0, -1.0);

  for (const sign of [-1, 1]) {
    const fHole = new THREE.Path();
    const cx = sign * 0.19;
    fHole.moveTo(cx - sign * 0.02, -0.22);
    fHole.bezierCurveTo(cx + sign * 0.03, -0.10, cx + sign * 0.03, 0.14, cx - sign * 0.02, 0.24);
    fHole.bezierCurveTo(cx - sign * 0.05, 0.14, cx - sign * 0.05, -0.10, cx - sign * 0.02, -0.22);
    shape.holes.push(fHole);
  }

  const extrudeSettings: THREE.ExtrudeGeometryOptions = {
    depth: thickness,
    bevelEnabled: true,
    bevelSegments: 5,
    steps: 1,
    bevelSize: 0.035,
    bevelThickness: 0.04,
    curveSegments: 32,
  };

  const geom = new THREE.ExtrudeGeometry(shape, extrudeSettings);
  geom.center();
  return geom;
}

export interface ViolinAssemblyResult {
  group: THREE.Group;
  interactables: InteractiveElement[];
  pluckedDecay: number[];
  update: (elapsed: number, dt: number) => void;
}

export function createParametricViolin(
  uniforms: TemporalUniforms,
  scale = 1.0,
  labelPrefix = 'Luthier Violin'
): ViolinAssemblyResult {
  const group = new THREE.Group();
  const interactables: InteractiveElement[] = [];

  const varnishMat = new THREE.MeshStandardMaterial({
    color: 0xc45b28,
    emissive: 0x3b1406,
    emissiveIntensity: 0.22,
    roughness: 0.24,
    metalness: 0.22,
  });

  const ebonyMat = new THREE.MeshStandardMaterial({
    color: 0x1c1722,
    roughness: 0.32,
    metalness: 0.35,
  });

  const mapleBridgeMat = new THREE.MeshStandardMaterial({
    color: 0xe6be8a,
    roughness: 0.42,
    metalness: 0.12,
  });

  const goldInlayMat = new THREE.MeshStandardMaterial({
    color: 0xf4c46e,
    emissive: 0x8c5a18,
    emissiveIntensity: 0.22,
    roughness: 0.24,
    metalness: 0.85,
  });

  // 1. Soundbox
  const bodyGeo = createCremoneseViolinBodyGeometry(0.14);
  const bodyMesh = new THREE.Mesh(bodyGeo, varnishMat);
  bodyMesh.castShadow = true;
  group.add(bodyMesh);

  // Subtle Purfling gold rim outline on top face
  const innerChamberMat = new THREE.MeshStandardMaterial({
    color: 0x6e2e14,
    emissive: 0x4a1c08,
    emissiveIntensity: 0.3,
    side: THREE.DoubleSide,
  });
  const innerChamber = new THREE.Mesh(new THREE.PlaneGeometry(0.6, 0.8), innerChamberMat);
  group.add(innerChamber);

  // 2. Neck, Fingerboard & Logarithmic Spiral Scroll Volute
  const neckGeo = new THREE.BoxGeometry(0.12, 1.05, 0.07);
  const neck = new THREE.Mesh(neckGeo, mapleBridgeMat);
  neck.position.set(0, 1.38, 0.02);
  group.add(neck);

  const fingerboardGeo = new THREE.BoxGeometry(0.14, 1.18, 0.035);
  const fingerboard = new THREE.Mesh(fingerboardGeo, ebonyMat);
  fingerboard.position.set(0, 1.15, 0.085);
  group.add(fingerboard);

  const scrollPts: THREE.Vector3[] = [];
  for (let i = 0; i <= 48; i++) {
    const t = (i / 48) * Math.PI * 3.5;
    const r = 0.025 * Math.exp(0.16 * t);
    scrollPts.push(new THREE.Vector3(0, 1.98 + r * Math.sin(t), 0.04 + r * Math.cos(t)));
  }
  const scrollCurve = new THREE.CatmullRomCurve3(scrollPts);
  const scrollGeo = new THREE.TubeGeometry(scrollCurve, 40, 0.046, 10, false);
  const scrollMesh = new THREE.Mesh(scrollGeo, varnishMat);
  group.add(scrollMesh);

  for (let p = 0; p < 4; p++) {
    const side = p % 2 === 0 ? -1 : 1;
    const pegGeo = new THREE.CylinderGeometry(0.016, 0.024, 0.24, 10);
    pegGeo.rotateZ(Math.PI / 2);
    const peg = new THREE.Mesh(pegGeo, goldInlayMat);
    peg.position.set(side * 0.11, 1.72 + p * 0.065, 0.03);
    group.add(peg);
  }

  // 3. Tailpiece, Chinrest & Carved Acoustic Bridge
  const tailGeo = new THREE.CylinderGeometry(0.085, 0.032, 0.48, 12);
  const tailpiece = new THREE.Mesh(tailGeo, ebonyMat);
  tailpiece.position.set(0, -0.68, 0.12);
  group.add(tailpiece);

  const chinrestGeo = new THREE.SphereGeometry(0.18, 16, 12);
  chinrestGeo.scale(1.1, 0.65, 0.35);
  const chinrest = new THREE.Mesh(chinrestGeo, ebonyMat);
  chinrest.position.set(-0.24, -0.78, 0.11);
  group.add(chinrest);

  const bridgeGeo = new THREE.BoxGeometry(0.24, 0.032, 0.11);
  const bridge = new THREE.Mesh(bridgeGeo, mapleBridgeMat);
  bridge.position.set(0, -0.12, 0.13);
  group.add(bridge);

  // 4. Four Fine Vibrating Strings (G3, D4, A4, E5)
  const stringNotes = [
    { name: 'G3 (Sol)', freq: 196.0 },
    { name: 'D4 (Re)', freq: 293.66 },
    { name: 'A4 (La)', freq: 440.0 },
    { name: 'E5 (Mi)', freq: 659.25 },
  ];

  const stringMaterials: THREE.ShaderMaterial[] = [];
  const pluckedDecay = [0.12, 0.12, 0.12, 0.12];

  stringNotes.forEach((note, idx) => {
    const xOffset = (idx - 1.5) * 0.045;
    const strMat = createViolinStringMaterial(uniforms, idx);
    strMat.uniforms.uFrequency.value = note.freq;
    stringMaterials.push(strMat);

    const strGeo = new THREE.CylinderGeometry(0.0055, 0.0055, 2.35, 8, 48);
    const strMesh = new THREE.Mesh(strGeo, strMat);
    strMesh.position.set(xOffset, 0.56, 0.16);
    group.add(strMesh);

    const hitGeo = new THREE.CylinderGeometry(0.042, 0.042, 2.35, 8);
    const hitMat = new THREE.MeshBasicMaterial({ visible: false });
    const hitMesh = new THREE.Mesh(hitGeo, hitMat);
    hitMesh.position.copy(strMesh.position);
    group.add(hitMesh);

    interactables.push({
      object: hitMesh,
      kind: 'violin-string',
      title: `${labelPrefix} · String ${note.name}`,
      detail: 'Click to pluck this resonant string and send an acoustic wave across Isidora.',
      zhTitle: `克雷莫納小提琴 · 琴弦 ${note.name}`,
      zhDetail: '點擊撥響這根共鳴琴弦，讓泛音聲波傳遍伊西多拉的石塔。',
      motif: 'violins',
      frequency: note.freq,
      onHover: (hovered) => {
        if (hovered) {
          pluckedDecay[idx] = Math.max(pluckedDecay[idx], 0.5);
        }
      },
      onActivate: () => {
        pluckedDecay[idx] = 1.25;
      },
    });
  });

  group.scale.setScalar(scale);

  return {
    group,
    interactables,
    pluckedDecay,
    update: (_elapsed, dt) => {
      for (let i = 0; i < 4; i++) {
        pluckedDecay[i] = Math.max(0.12, pluckedDecay[i] - dt * 0.85);
        stringMaterials[i].uniforms.uExcite.value = pluckedDecay[i];
      }
    },
  };
}

export function createLuthierPavilion(
  uniforms: TemporalUniforms
): ViolinAssemblyResult {
  const group = new THREE.Group();
  const interactables: InteractiveElement[] = [];

  const archStoneMat = createStoneMaterial(uniforms, {
    baseDesireHex: 0x784e52,
    baseMemoryHex: 0x30405e,
    roughness: 0.52,
    metalness: 0.2,
  });

  const brassMat = new THREE.MeshStandardMaterial({
    color: 0xe5b567,
    roughness: 0.25,
    metalness: 0.82,
  });

  const deckGeo = new THREE.CylinderGeometry(3.2, 3.6, 0.45, 36);
  const deck = new THREE.Mesh(deckGeo, archStoneMat);
  deck.receiveShadow = true;
  group.add(deck);

  const archGeo = new THREE.TorusGeometry(2.65, 0.16, 14, 48, Math.PI);
  const archA = new THREE.Mesh(archGeo, archStoneMat);
  archA.position.set(0, 0.2, -0.7);
  group.add(archA);

  const archB = new THREE.Mesh(archGeo, brassMat);
  archB.position.set(0, 0.2, 0.7);
  archB.scale.setScalar(0.92);
  group.add(archB);

  const centerViolin = createParametricViolin(uniforms, 1.25, 'Cremonese Master Violin');
  centerViolin.group.position.set(0, 2.25, 0);
  centerViolin.group.rotation.set(-0.15, 0.22, -0.1);
  group.add(centerViolin.group);
  interactables.push(...centerViolin.interactables);

  const harpFreqs = [146.83, 220.0, 293.66, 329.63, 440.0, 587.33];
  const harpMaterials: THREE.ShaderMaterial[] = [];
  const harpDecays = harpFreqs.map(() => 0.14);

  harpFreqs.forEach((freq, i) => {
    const x = (i - 2.5) * 0.72;
    const h = Math.sqrt(Math.max(0.5, 2.55 * 2.55 - x * x));
    const mat = createViolinStringMaterial(uniforms, i);
    mat.uniforms.uFrequency.value = freq;
    harpMaterials.push(mat);

    const strGeo = new THREE.CylinderGeometry(0.011, 0.011, h, 8, 36);
    const strMesh = new THREE.Mesh(strGeo, mat);
    strMesh.position.set(x, 0.22 + h * 0.5, -0.7);
    group.add(strMesh);

    const hitGeo = new THREE.CylinderGeometry(0.12, 0.12, h, 8);
    const hitMesh = new THREE.Mesh(
      hitGeo,
      new THREE.MeshBasicMaterial({ visible: false })
    );
    hitMesh.position.copy(strMesh.position);
    group.add(hitMesh);

    interactables.push({
      object: hitMesh,
      kind: 'violin-string',
      title: `Luthier Arch Harmonic String #${i + 1}`,
      detail: `Tuned to ${freq.toFixed(1)} Hz — click or hover to bow the architectural string.`,
      zhTitle: `製琴拱廊共鳴弦 #${i + 1}`,
      zhDetail: `調音頻率 ${freq.toFixed(1)} Hz —— 點擊或滑過以拉響跨塔琴弦。`,
      motif: 'violins',
      frequency: freq,
      onHover: (hovered) => {
        if (hovered) harpDecays[i] = Math.max(harpDecays[i], 0.65);
      },
      onActivate: () => {
        harpDecays[i] = 1.4;
      },
    });
  });

  return {
    group,
    interactables,
    pluckedDecay: centerViolin.pluckedDecay,
    update: (elapsed, dt) => {
      centerViolin.update(elapsed, dt);
      centerViolin.group.position.y = 2.25 + Math.sin(elapsed * 1.1) * 0.06;
      for (let i = 0; i < harpMaterials.length; i++) {
        harpDecays[i] = Math.max(0.14, harpDecays[i] - dt * 0.75);
        harpMaterials[i].uniforms.uExcite.value = harpDecays[i];
      }
    },
  };
}

export function buildViolinSpecimen(uniforms: TemporalUniforms): SpecimenInstance {
  const violin = createParametricViolin(uniforms, 1.15, 'Specimen Violin');
  violin.group.position.set(0, 1.85, 0);
  violin.group.rotation.set(-0.08, 0.18, -0.06);

  const stats = countGeometryStats(violin.group);

  return {
    group: violin.group,
    update: (elapsed, dt) => {
      violin.update(elapsed, dt);
      violin.group.position.y = 1.85 + Math.sin(elapsed * 1.2) * 0.05;
    },
    dispose: () => {},
    meta: {
      vertices: stats.vertices,
      triangles: stats.triangles,
      formula: 'u(y, t) = A · sin(πy/L) · sin(2πf₀t) + 0.35A · sin(2πy/L) · cos(4πf₀t)',
      parameters: {
        'Soundbox Contour': '12-Segment Cubic Bézier Cremonese',
        'Scroll Volute': 'r(θ) = 0.025·e^(0.16θ)',
        'Open String Tuning': 'G3 (196Hz) · D4 (294Hz) · A4 (440Hz) · E5 (659Hz)',
        'Acoustic Apertures': 'Twin Parametric f-Holes',
      },
    },
  };
}
